/**
 * BrandSearchSelect
 * Seleção de marca com busca e rolagem (padrão ERPSelectSearchModal).
 * Suporta seleção única ou múltipla (multiple=true).
 */

import React, { useEffect, useMemo, useState } from 'react'
import { Tag, ChevronDown, Loader2, Check } from 'lucide-react'
import ERPSelectSearchModal, { ERPSelectModalItem, ERPSelectModalEmpty } from '../../Modal/ERPSelectSearchModal'
import './BrandSearchSelect.css'

function BrandSearchSelect({
  value = '',
  brands = [],
  onChange,
  label = 'Marca',
  placeholder = 'Selecionar marca...',
  modalTitle = 'Selecionar Marca',
  modalSubtitle = 'Busque ou role a lista para encontrar a marca',
  allowClear = true,
  disabled = false,
  multiple = false
}) {
  const [modalOpen, setModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [draftSelection, setDraftSelection] = useState([])

  const selectedValues = useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value.filter(Boolean) : []
    }
    return value ? [value] : []
  }, [multiple, value])

  const filteredBrands = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return brands
    return brands.filter((brand) => String(brand).toLowerCase().includes(term))
  }, [brands, searchTerm])

  const displayValue = useMemo(() => {
    if (!multiple) return value || ''
    if (selectedValues.length === 0) return ''
    if (brands.length > 0 && selectedValues.length === brands.length) {
      return `Todas as marcas (${brands.length})`
    }
    if (selectedValues.length === 1) return selectedValues[0]
    return `${selectedValues.length} marcas selecionadas`
  }, [multiple, value, selectedValues, brands.length])

  useEffect(() => {
    if (!modalOpen) return undefined

    if (multiple) {
      setDraftSelection(Array.isArray(value) ? value.filter(Boolean) : [])
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setModalOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
    // Inicializa draft apenas ao abrir o modal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, multiple])

  const handleSelectSingle = (brand) => {
    onChange?.(brand)
    setModalOpen(false)
    setSearchTerm('')
  }

  const handleToggleMultiple = (brand) => {
    setDraftSelection((prev) => (
      prev.includes(brand)
        ? prev.filter((item) => item !== brand)
        : [...prev, brand]
    ))
  }

  const handleConfirmMultiple = () => {
    onChange?.(draftSelection)
    setModalOpen(false)
    setSearchTerm('')
  }

  const handleSelectAll = () => {
    setDraftSelection([...brands])
  }

  const handleClear = () => {
    if (multiple) {
      setDraftSelection([])
      onChange?.([])
    } else {
      onChange?.('')
    }
    setModalOpen(false)
    setSearchTerm('')
  }

  const isSelected = (brand) => (
    multiple ? draftSelection.includes(brand) : value === brand
  )

  return (
    <>
      <div className={`brand-search-select${disabled ? ' is-disabled' : ''}`}>
        {label && <label className="brand-search-select__label">{label}</label>}
        <div className="brand-search-select__wrapper">
          <Tag size={18} className="brand-search-select__icon" />
          <input
            type="text"
            readOnly
            disabled={disabled}
            className="brand-search-select__input"
            placeholder={placeholder}
            value={displayValue}
            onClick={() => !disabled && setModalOpen(true)}
          />
          <ChevronDown size={14} className="brand-search-select__chevron" />
        </div>
      </div>

      <ERPSelectSearchModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setSearchTerm('')
        }}
        title={modalTitle}
        subtitle={modalSubtitle}
        icon={<Tag size={24} />}
        searchPlaceholder="Buscar marca..."
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        footer={(
          <div className="brand-search-select__footer">
            {multiple && brands.length > 0 ? (
              <button
                type="button"
                className="erp-select-modal-btn-secondary"
                onClick={handleSelectAll}
              >
                Selecionar todas
              </button>
            ) : null}
            {allowClear && selectedValues.length > 0 ? (
              <button
                type="button"
                className="erp-select-modal-btn-secondary"
                onClick={handleClear}
              >
                Limpar seleção
              </button>
            ) : null}
            {multiple ? (
              <button
                type="button"
                className="brand-search-select__confirm-btn"
                onClick={handleConfirmMultiple}
                disabled={draftSelection.length === 0}
              >
                Confirmar ({draftSelection.length})
              </button>
            ) : null}
          </div>
        )}
      >
        {filteredBrands.length > 0 ? (
          filteredBrands.map((brand) => (
            <ERPSelectModalItem
              key={brand}
              selected={isSelected(brand)}
              onClick={() => (
                multiple ? handleToggleMultiple(brand) : handleSelectSingle(brand)
              )}
              avatar={<Tag size={20} />}
              name={brand}
              checkIcon={<Check size={18} className="erp-select-modal-item-check" />}
            />
          ))
        ) : (
          <ERPSelectModalEmpty
            icon={brands.length === 0 ? <Tag size={32} /> : <Loader2 size={32} className="spinning" />}
            message={
              brands.length === 0
                ? 'Nenhuma marca disponível'
                : searchTerm.trim()
                  ? 'Nenhuma marca encontrada'
                  : 'Digite para buscar marcas'
            }
          />
        )}
      </ERPSelectSearchModal>
    </>
  )
}

export default BrandSearchSelect
